import { useState, useEffect, useCallback } from "react";
import { ReservationSuccessAnimation } from "@/components/ReservationSuccessAnimation";
import { Link } from "react-router-dom";
import { gtag } from "@/utils/gtag";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, Users, Ruler, Calendar, ChevronRight, Star, Plus } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ReservationDialog } from "@/components/ReservationDialog";
import { ChateauSelector } from "@/components/ChateauSelector";
import { ChateauCard } from "@/components/ChateauCard";
import { SumoProductCard } from "@/components/SumoProductCard";
import { useAdminAuth } from "@/components/AdminAuth";
import { AdminPanel } from "@/components/AdminPanel";
import { useChateaux } from "@/hooks/useChateaux";
import { useSumos } from "@/hooks/useSumos";
import { ChateauFormDialog } from "@/components/ChateauFormDialog";
import { AdvancedFilterControls } from "@/components/enhanced/AdvancedFilterControls";
import { useAdvancedChateauFiltering } from "@/hooks/useAdvancedChateauFiltering";
import { TrustIndicators } from "@/components/enhanced/TrustIndicators";
import { EnhancedSEOOptimizer } from "@/utils/enhancedSeoOptimizer";
import { DateRangePicker } from "@/components/DateRangePicker";
import { WeatherPreview } from "@/components/WeatherPreview";
import { useAvailability } from "@/hooks/useAvailability";
import { format } from "date-fns";

export const Chateaux = () => {
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [selectedChateau, setSelectedChateau] = useState<string>("");
  const [dateDebut, setDateDebut] = useState<Date | undefined>();
  const [dateFin, setDateFin] = useState<Date | undefined>();
  const [showSuccess, setShowSuccess] = useState(false);
  
  const handleReservationSuccess = useCallback(() => {
    setReservationDialogOpen(false);
    setShowSuccess(true);
  }, []);
  
  const dateDebutStr = dateDebut ? format(dateDebut, 'yyyy-MM-dd') : undefined;
  const dateFinStr = dateFin ? format(dateFin, 'yyyy-MM-dd') : undefined;
  const { isProductAvailable, getAvailableCount, hasDatesSelected } = useAvailability(dateDebutStr, dateFinStr);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const {
    isAdminLoggedIn
  } = useAdminAuth();
  const {
    chateaux,
    loading,
    createChateau
  } = useChateaux();
  const {
    sumos,
    loading: sumosLoading
  } = useSumos();

  // Enhanced filtering system
  const {
    criteria,
    setCriteria,
    filteredChateaux,
    resetFilters,
    hasActiveFilters,
    resultsCount,
    spaceOptions,
    budgetOptions,
    ageOptions,
    themeOptions,
    sizeOptions,
    featureOptions
  } = useAdvancedChateauFiltering({
    chateaux
  });

  // Enhanced SEO data
  const seoData = EnhancedSEOOptimizer.generatePageSEO('listing', {
    count: resultsCount,
    images: []
  });

  // Track page view and products for Google Ads
  useEffect(() => {
    // Track page view
    gtag('event', 'page_view', {
      page_title: 'Châteaux Gonflables - Château de Clémentine',
      page_location: window.location.href,
      send_to: 'AW-1049756763'
    });

    // Track view_item_list for remarketing
    if (chateaux && chateaux.length > 0) {
      gtag('event', 'view_item_list', {
        item_list_id: 'chateaux_gonflables',
        item_list_name: 'Châteaux Gonflables',
        items: chateaux.map((chateau, index) => ({
          item_id: chateau.id,
          item_name: chateau.nom,
          item_category: 'Château Gonflable',
          price: parseInt(chateau.prix) || 60,
          index: index
        }))
      });
    }
  }, [chateaux]);
  const handleCreateChateau = async (chateauData: any) => {
    await createChateau(chateauData);
  };
  const handleReservation = (chateauId: string) => {
    setSelectedChateau(chateauId);
    setReservationDialogOpen(true);
  };
  return <>
      <SEO title={seoData.title} description={seoData.description} keywords={seoData.keywords.join(', ')} canonicalUrl={seoData.canonicalUrl} ogImage={seoData.ogImage} schema={seoData.schema} />
      <Header />
      <main className="min-h-screen bg-gradient-sky">
      {/* Hero Section */}
      <section className="py-16 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-display font-bold mb-6">
            <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Découvrez Tous Nos Produits
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
            Châteaux gonflables et sumos pour tous les goûts et tous les âges. 
            Trouvez l'animation parfaite pour votre événement !
          </p>
          <div className="flex flex-wrap gap-4 justify-center text-sm">
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Star className="w-4 h-4 mr-2 text-accent" />
              <span className="font-medium">À partir de 30€</span>
            </div>
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Calendar className="w-4 h-4 mr-2 text-primary" />
              <span className="font-medium">Week-end complet</span>
            </div>
            <div className="flex items-center bg-white/90 rounded-full px-4 py-2">
              <Ruler className="w-4 h-4 mr-2 text-secondary" />
              <span className="font-medium">Différentes tailles</span>
            </div>
          </div>
          
          {/* Admin Add Product Button */}
          {isAdminLoggedIn && <div className="mt-8 flex gap-4 justify-center">
              <ChateauFormDialog onSave={handleCreateChateau} trigger={<Button variant="hero" size="lg">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter un Château
                  </Button>} title="Ajouter un Nouveau Château" />
              <Button variant="outline" size="lg" onClick={() => setAdminPanelOpen(true)}>
                Panneau Admin
              </Button>
            </div>}
        </div>
      </section>

      {/* Date Filter + Advanced Filters */}
      <section className="pb-8">
        <div className="container mx-auto px-4 space-y-4">
          <Card className="shadow-gentle border-0 border-2 border-primary/20">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="w-5 h-5 text-primary" />
                    <h2 className="text-lg font-display font-bold text-primary">
                      Commencez par choisir vos dates
                    </h2>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Sélectionnez les dates de votre événement pour voir les produits disponibles.
                  </p>
                  <DateRangePicker
                    dateDebut={dateDebut}
                    dateFin={dateFin}
                    onDateDebutChange={setDateDebut}
                    onDateFinChange={setDateFin}
                    label=""
                  />
                  {hasDatesSelected && (
                    <p className="text-sm text-primary font-medium mt-3">
                      ✅ Seuls les produits disponibles pour ces dates sont affichés.
                    </p>
                  )}
                </div>
                <WeatherPreview dateDebut={dateDebut} dateFin={dateFin} />
              </div>
            </CardContent>
          </Card>
          <AdvancedFilterControls criteria={criteria} onCriteriaChange={setCriteria} spaceOptions={spaceOptions} budgetOptions={budgetOptions} ageOptions={ageOptions} themeOptions={themeOptions} sizeOptions={sizeOptions} featureOptions={featureOptions} onReset={resetFilters} hasActiveFilters={hasActiveFilters} resultsCount={resultsCount} collapsible />
        </div>
      </section>

      {/* Trust Indicators */}
      

      {/* Products Grid */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          {(loading || sumosLoading) ? <div className="text-center py-12">
              <p className="text-muted-foreground">Chargement des produits...</p>
            </div> : <>
              {/* Châteaux */}
              {filteredChateaux.filter(chateau => !hasDatesSelected || isProductAvailable(chateau.id, 'chateau')).length > 0 && (
                <>
                  <h2 className="text-2xl font-display font-bold text-primary mb-6">🏰 Châteaux Gonflables</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                    {filteredChateaux
                      .filter(chateau => !hasDatesSelected || isProductAvailable(chateau.id, 'chateau'))
                      .map(chateau => <ChateauCard key={chateau.id} chateau={{
                      ...chateau,
                      ageRecommande: chateau.age_recommande,
                      imagePosition: chateau.image_position ?? 50,
                      quantite: chateau.quantite
                    }} onReservation={handleReservation} hasDatesSelected={hasDatesSelected} availableCount={getAvailableCount(chateau.id, 'chateau')} />)}
                  </div>
                </>
              )}

              {/* Sumos */}
              {sumos.filter(sumo => !hasDatesSelected || isProductAvailable(sumo.id, 'sumo')).length > 0 && (
                <>
                  <h2 className="text-2xl font-display font-bold text-primary mb-6">🥋 Sumos Gonflables</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {sumos
                      .filter(sumo => !hasDatesSelected || isProductAvailable(sumo.id, 'sumo'))
                      .map(sumo => <SumoProductCard key={sumo.id} sumo={{
                      ...sumo,
                      quantite: sumo.quantite
                    }} onReservation={handleReservation} hasDatesSelected={hasDatesSelected} availableCount={getAvailableCount(sumo.id, 'sumo')} />)}
                  </div>
                </>
              )}
            </>}
          
          {!loading && !sumosLoading && filteredChateaux.length === 0 && sumos.length === 0 && <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                Aucun produit ne correspond à vos critères.
              </p>
              <Button variant="outline" onClick={resetFilters} className="mt-4">
                Réinitialiser les filtres
              </Button>
            </div>}
        </div>
      </section>

      {/* Help Choosing Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <ChateauSelector />
          </div>
        </div>
      </section>
      </main>
      <Footer />
      
      <ReservationDialog 
        open={reservationDialogOpen} 
        onOpenChange={setReservationDialogOpen} 
        preSelectedChateau={selectedChateau}
        initialDateDebut={dateDebut}
        initialDateFin={dateFin}
        onSuccess={handleReservationSuccess}
      />
      
      {showSuccess && (
        <ReservationSuccessAnimation onComplete={() => setShowSuccess(false)} />
      )}
      
      <AdminPanel isOpen={adminPanelOpen} onClose={() => setAdminPanelOpen(false)} />
    </>;
};